import React, { useState } from 'react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Activity, ArrowLeft, ArrowRight, Calendar, Check, ChevronDown, ChevronUp, Clock, Target, User } from 'lucide-react';
import { RecoveryPlan, ProgressSnapshot } from '../RecoveryMonitoring';

interface RecoveryProgressProps {
  recoveryPlan: RecoveryPlan;
  selectedSnapshotId: string;
  onSnapshotChange: (snapshotId: string) => void;
}

// Helper function to format dates
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const RecoveryProgress: React.FC<RecoveryProgressProps> = ({ 
  recoveryPlan, 
  selectedSnapshotId, 
  onSnapshotChange 
}) => {
  const [expandedExercises, setExpandedExercises] = useState<boolean>(false);
  const [metricView, setMetricView] = useState<'radar' | 'timeline'>('radar');
  
  // Find the selected snapshot
  const selectedSnapshot = recoveryPlan.progressSnapshots.find(
    snapshot => snapshot.id === selectedSnapshotId
  ) || recoveryPlan.progressSnapshots[recoveryPlan.progressSnapshots.length - 1];
  
  // Find current phase
  const currentPhase = recoveryPlan.phases[recoveryPlan.currentPhase];
  
  // Find index of selected snapshot
  const selectedSnapshotIndex = recoveryPlan.progressSnapshots.findIndex(
    snapshot => snapshot.id === selectedSnapshotId
  );
  
  // Get previous and next snapshot IDs
  const previousSnapshotId = selectedSnapshotIndex > 0 
    ? recoveryPlan.progressSnapshots[selectedSnapshotIndex - 1].id 
    : null;
  
  const nextSnapshotId = selectedSnapshotIndex < recoveryPlan.progressSnapshots.length - 1 
    ? recoveryPlan.progressSnapshots[selectedSnapshotIndex + 1].id 
    : null;
  
  // Format time since recovery started
  const getWeeksSinceStart = (date: Date): string => {
    const diffTime = Math.abs(date.getTime() - recoveryPlan.recoveryStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const remainingDays = diffDays % 7;
    
    return `Week ${diffWeeks}${remainingDays > 0 ? `, Day ${remainingDays}` : ''}`;
  };
  
  // Prepare data for radar chart
  const prepareRadarData = (snapshot: ProgressSnapshot) => {
    const phase = recoveryPlan.phases[snapshot.phase];
    const baselineMetrics = recoveryPlan.baselineMetrics;
    
    return Object.keys(snapshot.metrics).map(key => {
      const currentValue = snapshot.metrics[key];
      const targetValue = phase.targetMetrics[key];
      const baselineValue = baselineMetrics ? baselineMetrics[key as keyof typeof baselineMetrics] : undefined;
      
      return {
        metric: key,
        value: currentValue,
        target: targetValue,
        baseline: baselineValue,
        // Convert metric name to more readable format
        fullName: key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
      };
    });
  };
  
  // Prepare data for timeline chart
  const prepareTimelineData = (selectedMetric: string) => {
    return recoveryPlan.progressSnapshots.map(snapshot => {
      const phase = recoveryPlan.phases[snapshot.phase];
      
      return {
        date: formatDate(snapshot.date),
        timestamp: snapshot.date.getTime(),
        value: snapshot.metrics[selectedMetric],
        target: phase.targetMetrics[selectedMetric],
        baseline: recoveryPlan.baselineMetrics ? recoveryPlan.baselineMetrics[selectedMetric as keyof typeof recoveryPlan.baselineMetrics] : undefined,
        week: getWeeksSinceStart(snapshot.date),
        isSelected: snapshot.id === selectedSnapshotId
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  };
  
  // Selected metrics to show in the timeline view
  const [selectedTimelineMetric, setSelectedTimelineMetric] = useState<string>('symmetryIndex');
  
  const radarData = prepareRadarData(selectedSnapshot);
  const timelineData = prepareTimelineData(selectedTimelineMetric);
  
  // Calculate percentage of target achieved for each metric
  const calculatePercentageOfTarget = (metric: string, value: number): number => {
    const targetValue = recoveryPlan.phases[selectedSnapshot.phase].targetMetrics[metric];
    if (targetValue === 0) return 0;
    return Math.round((value / targetValue) * 100);
  };
  
  // Sort metrics by percentage of target achieved
  const sortedMetrics = Object.keys(selectedSnapshot.metrics)
    .map(metric => ({
      name: metric,
      value: selectedSnapshot.metrics[metric],
      percentage: calculatePercentageOfTarget(metric, selectedSnapshot.metrics[metric]),
      fullName: metric
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
    }))
    .sort((a, b) => a.percentage - b.percentage);
  
  return (
    <div className="p-6">
      {/* Selected Snapshot Navigation */}
      <div className="flex justify-between items-center mb-6 bg-gray-50 rounded-lg p-4">
        <button
          onClick={() => previousSnapshotId && onSnapshotChange(previousSnapshotId)}
          disabled={!previousSnapshotId}
          className={`p-2 rounded ${!previousSnapshotId ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
        >
          <ArrowLeft size={20} />
        </button>
        
        <div className="text-center">
          <h3 className="font-medium">
            {getWeeksSinceStart(selectedSnapshot.date)}
          </h3>
          <p className="text-sm text-gray-600">{formatDate(selectedSnapshot.date)}</p>
        </div>
        
        <button
          onClick={() => nextSnapshotId && onSnapshotChange(nextSnapshotId)}
          disabled={!nextSnapshotId}
          className={`p-2 rounded ${!nextSnapshotId ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
        >
          <ArrowRight size={20} />
        </button>
      </div>
      
      {/* Patient reported metrics */}
      <div className="bg-white rounded-lg border p-4 mb-6">
        <h3 className="font-medium mb-4 flex items-center">
          <User className="mr-2 text-blue-500" size={20} />
          Patient Reported Metrics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-700 text-sm">Pain Level</span>
              <span className="text-sm font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded">
                {selectedSnapshot.patientReported.painLevel}/10
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-red-500 h-2.5 rounded-full" 
                style={{ width: `${(selectedSnapshot.patientReported.painLevel / 10) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedSnapshot.patientReported.painLevel <= 3 ? 'Low pain, good progress' :
               selectedSnapshot.patientReported.painLevel <= 6 ? 'Moderate pain, monitor closely' :
               'High pain, consider intervention'}
            </p>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-700 text-sm">Confidence</span>
              <span className="text-sm font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded">
                {selectedSnapshot.patientReported.confidence}/10
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-green-500 h-2.5 rounded-full" 
                style={{ width: `${(selectedSnapshot.patientReported.confidence / 10) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedSnapshot.patientReported.confidence >= 7 ? 'High confidence, good sign' :
               selectedSnapshot.patientReported.confidence >= 4 ? 'Building confidence' :
               'Low confidence, may need encouragement'}
            </p>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-700 text-sm">Difficulty</span>
              <span className="text-sm font-medium bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                {selectedSnapshot.patientReported.difficulty}/10
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-yellow-500 h-2.5 rounded-full" 
                style={{ width: `${(selectedSnapshot.patientReported.difficulty / 10) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {selectedSnapshot.patientReported.difficulty <= 3 ? 'Low difficulty, good progress' :
               selectedSnapshot.patientReported.difficulty <= 6 ? 'Moderate difficulty, expected' :
               'High difficulty, may need adjustment'}
            </p>
          </div>
        </div>
        
        {selectedSnapshot.notes && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-700 mb-1">Assessment Notes</h4>
            <p className="text-sm text-gray-600">{selectedSnapshot.notes}</p>
          </div>
        )}
      </div>
      
      {/* Metrics Visualization Switcher */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Recovery Metrics</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setMetricView('radar')}
            className={`px-3 py-1 text-sm rounded ${
              metricView === 'radar' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Radar View
          </button>
          <button
            onClick={() => setMetricView('timeline')}
            className={`px-3 py-1 text-sm rounded ${
              metricView === 'timeline' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Timeline View
          </button>
        </div>
      </div>
      
      {/* Metrics Visualization */}
      <div className="bg-white rounded-lg border mb-6">
        {metricView === 'radar' ? (
          <div className="p-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="fullName" />
                  <PolarRadiusAxis domain={[0, 100]} />
                  <Radar
                    name="Current"
                    dataKey="value"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.3}
                  />
                  <Radar
                    name="Target"
                    dataKey="target"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.2}
                  />
                  {recoveryPlan.baselineMetrics && (
                    <Radar
                      name="Baseline (Pre-injury)"
                      dataKey="baseline"
                      stroke="#6366f1"
                      fill="#6366f1"
                      fillOpacity={0.1}
                    />
                  )}
                  <Legend />
                  <Tooltip 
                    formatter={(value) => [`${Number(value).toFixed(1)}`, '']}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sortedMetrics.map(metric => (
                <div key={metric.name} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{metric.fullName}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      metric.percentage >= 100 ? 'bg-green-100 text-green-800' :
                      metric.percentage >= 75 ? 'bg-blue-100 text-blue-800' :
                      metric.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {metric.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                    <div 
                      className={`h-1.5 rounded-full ${
                        metric.percentage >= 100 ? 'bg-green-500' :
                        metric.percentage >= 75 ? 'bg-blue-500' :
                        metric.percentage >= 50 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, metric.percentage)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Current: {metric.value.toFixed(1)}</span>
                    <span className="text-xs text-gray-500">Target: {recoveryPlan.phases[selectedSnapshot.phase].targetMetrics[metric.name].toFixed(1)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="mb-4">
              <label htmlFor="metric-select" className="block text-sm font-medium text-gray-700 mb-1">
                Select Metric to View
              </label>
              <select
                id="metric-select"
                className="w-full md:w-64 p-2 border rounded-md"
                value={selectedTimelineMetric}
                onChange={(e) => setSelectedTimelineMetric(e.target.value)}
              >
                {Object.keys(selectedSnapshot.metrics).map(metric => (
                  <option key={metric} value={metric}>
                    {metric
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={timelineData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      `${Number(value).toFixed(1)}`, 
                      name === 'value' ? 'Current' : 
                      name === 'target' ? 'Target' : 
                      'Baseline (Pre-injury)'
                    ]}
                    labelFormatter={(label) => {
                      const item = timelineData.find(item => item.date === label);
                      return item ? `${item.week} - ${label}` : label;
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    name="Current" 
                    stroke="#3b82f6" 
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                    dot={{
                      r: 4,
                      fill: "#3b82f6",
                      strokeWidth: 2,
                      stroke: "white"
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="target" 
                    name="Target" 
                    stroke="#10b981" 
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                  />
                  {recoveryPlan.baselineMetrics && (
                    <Line 
                      type="monotone" 
                      dataKey="baseline" 
                      name="Baseline (Pre-injury)" 
                      stroke="#6366f1" 
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      
      {/* Exercises Section */}
      <div className="bg-white rounded-lg border">
        <div 
          className="p-4 border-b flex justify-between items-center cursor-pointer"
          onClick={() => setExpandedExercises(!expandedExercises)}
        >
          <h3 className="font-medium flex items-center">
            <Activity className="mr-2 text-indigo-500" size={20} />
            Current Phase Exercises
          </h3>
          <button className="text-gray-500">
            {expandedExercises ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
        
        {expandedExercises && (
          <div className="p-4">
            <p className="text-gray-600 mb-4">
              Recommended exercises for <span className="font-medium">{currentPhase.name}</span>: {currentPhase.description}
            </p>
            <div className="space-y-4">
              {currentPhase.exercises.map(exercise => (
                <div key={exercise.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between">
                    <h4 className="font-medium">{exercise.name}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      exercise.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {exercise.completed ? 
                        <span className="flex items-center"><Check size={12} className="mr-1" /> Completed</span> : 
                        'In Progress'}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1 text-sm">{exercise.description}</p>
                  <div className="mt-2 text-sm text-gray-500 flex space-x-4">
                    <span className="flex items-center">
                      <Clock size={14} className="mr-1" /> {exercise.duration}
                    </span>
                    <span className="flex items-center">
                      <Calendar size={14} className="mr-1" /> {exercise.frequency}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecoveryProgress; 